import { Box, Inline } from '@atlaskit/primitives';
import React from 'react';
import { proxy } from 'valtio';

export const isDefaultMessage = (message: string | readonly []) => message === '';

export interface SnackbarMessageProps {
  linkButton(
    onClick: (event: React.MouseEvent<HTMLElement>) => void,
    linkMessage: string
  ): React.ReactElement;
}

interface SnackbarAction {
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
  content: string;
}

export const getMessage = (
  message: string | readonly [],
  actions?: ReadonlyArray<SnackbarAction>
): string | React.FC<SnackbarMessageProps> | readonly [] => {
  if (!actions || actions.length === 0 || message === '') {
    return message;
  }

  return function SnackbarMessage({ linkButton }) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', maxWidth: '560px' }}>
        <span>{message}</span> &nbsp;
        <Inline space="space.050">
          {actions.map((action, index) => (
            <Box key={index}>{linkButton(action.onClick, action.content)}</Box>
          ))}
        </Inline>
      </div>
    );
  };
};

interface MessageOptions {
  id?: string;
  hideAfter?: number;
  showAfter?: number;
  actions?: SnackbarAction[];
  showCloseButton?: boolean;
  i18nVariables?: Record<string, string | number>;
  progress?: number;
  onDismiss?: () => void;
  showIcon?: boolean;
  type?: 'info' | 'success' | 'warning' | 'error';
}

interface SnackbarState {
  /* Used to clear this specific snackbar message. */
  id?: string;
  message: string | [];
  /* Hide the snackbar after a certain amount of time. */
  hideTimeout?: NodeJS.Timeout;
  /* Delay showing the snackbar for a certain amount of time. */
  showTimeout?: NodeJS.Timeout;
  actions?: SnackbarAction[];
  showCloseButton: boolean;
  i18nVariables: Record<string, string | number>;
  /**
   * A number between 0 and 1 representing the progress of the snackbar.
   * Note: If you want to reset the loading bar without a transition, do not
   * define progress in the next setMessage(), or set this to undefined.
   */
  progress?: number;
  /** Called when the user manually dismisses the flag. */
  onDismiss(): void;
  /* Show the icon corresponding to the snackbar type. */
  showIcon: boolean;
  type?: 'info' | 'success' | 'warning' | 'error';

  clearMessage(messageId?: string): void;
  setProgress(progress: number): void;
  setMessage(message: string, options?: MessageOptions): void;
  onClose(): void;
}

export const currentSnackbarState = proxy<SnackbarState>({
  id: '',
  message: '',
  hideTimeout: undefined,
  showTimeout: undefined,
  actions: undefined,
  showCloseButton: false,
  i18nVariables: {},
  progress: undefined,
  onDismiss: () => undefined,
  showIcon: false,
  type: undefined,

  clearMessage(messageId) {
    if (messageId && messageId !== currentSnackbarState.id) {
      return;
    }
    clearSnackbarState(false);
  },

  setProgress(progress: number) {
    currentSnackbarState.progress = progress;
  },

  setMessage(message: string, options: MessageOptions) {
    if (typeof options?.showAfter !== 'number') {
      handleSnackbarUpdate(message, options);
    } else {
      currentSnackbarState.showTimeout = setTimeout(
        () => handleSnackbarUpdate(message, options),
        options.showAfter
      );
    }
  },

  onClose() {
    currentSnackbarState.onDismiss();
    clearSnackbarState(true);
  },
});

function clearSnackbarState(clearShowTimeout: boolean) {
  if (currentSnackbarState.hideTimeout) {
    clearTimeout(currentSnackbarState.hideTimeout);
  }

  if (clearShowTimeout && currentSnackbarState.showTimeout) {
    clearTimeout(currentSnackbarState.showTimeout);
    currentSnackbarState.showTimeout = undefined;
  }

  currentSnackbarState.id = '';
  currentSnackbarState.message = '';
  currentSnackbarState.hideTimeout = undefined;
  currentSnackbarState.actions = undefined;
  currentSnackbarState.showCloseButton = false;
  currentSnackbarState.i18nVariables = {};
  currentSnackbarState.progress = undefined;
  currentSnackbarState.onDismiss = () => undefined;
  currentSnackbarState.showIcon = false;
  currentSnackbarState.type = undefined;
}

function handleSnackbarUpdate(message: string | [], options?: MessageOptions) {
  clearSnackbarState(true);
  currentSnackbarState.progress = options?.progress;
  currentSnackbarState.message = message;
  currentSnackbarState.showCloseButton = options?.showCloseButton || false;
  currentSnackbarState.showIcon = options?.showIcon || false;
  currentSnackbarState.type = options?.type;
  currentSnackbarState.i18nVariables = options?.i18nVariables || {};
  currentSnackbarState.actions = options?.actions;
  currentSnackbarState.onDismiss = options?.onDismiss || (() => undefined);
  currentSnackbarState.id = options?.id;

  if (typeof options?.hideAfter === 'number') {
    currentSnackbarState.hideTimeout = setTimeout(() => {
      clearSnackbarState(false);
    }, options.hideAfter);
  }
}
